/**
 * Password Management Modal Component
 * 
 * Allows admins to manage user passwords with three options:
 * 1. Send password reset email (temp password emailed; user changes at next login)
 * 2. Set password directly
 * 3. Force password reset (Cognito emails a verification code; user resets before next login)
 * 
 * Requirements: Phase 3 - Admin Password Management
 */

import { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { restClient, User } from '../../api/rest-client';

interface PasswordManagementModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ResetMode = 'email' | 'set' | 'force';

interface PasswordRequirement {
  text: string;
  test: (password: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { text: 'At least 8 characters', test: (p) => p.length >= 8 },
  { text: 'One uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { text: 'One lowercase letter (a-z)', test: (p) => /[a-z]/.test(p) },
  { text: 'One number (0-9)', test: (p) => /\d/.test(p) },
  { text: 'One special character (!@#$%^&*)', test: (p) => /[!@#$%^&*]/.test(p) },
];

export default function PasswordManagementModal({
  user,
  isOpen,
  onClose,
  onSuccess,
}: PasswordManagementModalProps) {
  const [mode, setMode] = useState<ResetMode>('email');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Reset form when modal closes or opens
  useEffect(() => {
    if (!isOpen) {
      setMode('email');
      setPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  // Validate password for "set" mode
  const validatePassword = (): boolean => {
    if (mode !== 'set') return true;

    if (!password) {
      setError('Password is required');
      return false;
    }

    const failedRequirements = PASSWORD_REQUIREMENTS.filter(
      req => !req.test(password)
    );
    if (failedRequirements.length > 0) {
      setError('Password does not meet all requirements');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!validatePassword()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === 'email') {
        await restClient.sendPasswordResetEmail(user.username);
        setSuccess(`Password reset email sent to ${user.email}`);
      } else if (mode === 'set') {
        await restClient.setUserPassword(user.username, password, true);
        setSuccess('Password set successfully');
      } else if (mode === 'force') {
        await restClient.forcePasswordReset(user.username);
        setSuccess('Password reset initiated. The user has been emailed a verification code to set a new password.');
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Password management error:', err);
      setError(err.message || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check which requirements are met
  const requirementsMet = PASSWORD_REQUIREMENTS.map(req => ({
    ...req,
    met: req.test(password),
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Password Management - ${user.name || user.email}`}
    >
      {success ? (
        <div className="text-center py-8">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Success!</h3>
          <p className="text-sm text-gray-600">{success}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Mode Selection */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                checked={mode === 'email'}
                onChange={() => setMode('email')}
                className="mt-1"
              />
              <div>
                <span className="font-medium text-gray-900">Send Password Reset Email</span>
                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                  Recommended
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  Forces user to reset password at next login. User receives email notification. Most secure option.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                checked={mode === 'set'}
                onChange={() => setMode('set')}
                className="mt-1"
              />
              <div>
                <span className="font-medium text-gray-900">Set Password Directly</span>
                <p className="text-sm text-gray-600 mt-1">
                  Set a new password for the user immediately. Use for emergency access.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                checked={mode === 'force'}
                onChange={() => setMode('force')}
                className="mt-1"
              />
              <div>
                <span className="font-medium text-gray-900">Force Password Reset</span>
                <p className="text-sm text-gray-600 mt-1">
                  Requires the user to reset their password. Cognito emails them a verification code to set a new password before their next login.
                </p>
              </div>
            </label>
          </div>

          {/* Password Fields (only for 'set' mode) */}
          {mode === 'set' && (
            <div className="space-y-3 pt-4 border-t">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password *
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
              </div>

              {/* Password Requirements */}
              {password && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs font-medium text-gray-700">Password must contain:</p>
                  {requirementsMet.map((req, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      {req.met ? (
                        <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="h-4 w-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={req.met ? 'text-green-700' : 'text-gray-600'}>{req.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Confirm'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
